import { Value } from "@sinclair/typebox/value";
import { type BodyOf, ROUTES, type RouteById, type SuccessOf, Tag, VideoState } from "@volgo/core/contract";
import { type ApiClient, ApiError, isAccessRejection } from "../api/client";
import { readPref, writePref } from "../ui/prefs";
import { fileError, UploadError, type UploadTicket, uploadFile } from "./upload";

export type VideoFormValue = BodyOf<RouteById<"videos.update">> & {
  tagText?: string;
  gidText?: string;
  episodeText?: string;
};
export type UploadPhase =
  | "draft"
  | "queued"
  | "creating"
  | "uploading"
  | "paused"
  | "missing"
  | "ambiguous"
  | "transferred"
  | "processing"
  | "published"
  | "failed"
  | "cancel-failed"
  | "cancelled"
  | "error";
type ServerState = SuccessOf<RouteById<"videos.status">>;
export type UploadRow = {
  id: string;
  name: string;
  size: number;
  modified: number;
  form: VideoFormValue;
  phase: UploadPhase;
  target: boolean;
  done: number;
  rate: number;
  file?: File;
  ticket?: UploadTicket;
  state?: ServerState;
  error?: string;
  duplicate?: boolean;
  legacy?: boolean;
  codec?: string | null;
  retryOf?: string;
};
export type QueueSnapshot = {
  rows: UploadRow[];
  running: boolean;
  stopReason: string;
  storageWarning: string;
};
const phases: UploadPhase[] = [
  "draft",
  "queued",
  "creating",
  "uploading",
  "paused",
  "missing",
  "ambiguous",
  "transferred",
  "processing",
  "published",
  "failed",
  "cancel-failed",
  "cancelled",
  "error",
];
const message = (error: unknown) => (error instanceof Error ? error.message : "요청을 완료하지 못했습니다.");
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);
const integer = (n: unknown, max = Number.MAX_SAFE_INTEGER): n is number =>
  typeof n === "number" && Number.isSafeInteger(n) && n >= 0 && n <= max;
export const editable = (row: UploadRow) =>
  !row.ticket && !["creating", "ambiguous", "cancelled"].includes(row.phase);
export const matchesFile = (row: Pick<UploadRow, "name" | "size" | "modified">, file: File) =>
  row.name === file.name && row.size === file.size && row.modified === file.lastModified;
export const needsFile = (row: UploadRow) =>
  !row.file &&
  !["transferred", "processing", "published", "cancelled", "ambiguous", "failed"].includes(row.phase);
export const needsLeaveWarning = (row: UploadRow) =>
  !["transferred", "processing", "published", "cancelled", "failed"].includes(row.phase) &&
  (!!row.file || !!row.ticket || row.phase === "creating" || row.phase === "ambiguous");

export function finalizeForm(form: VideoFormValue): BodyOf<RouteById<"videos.update">> & { title: string } {
  const title = form.title?.trim() ?? "";
  if (!title || title.length > 120) throw new Error("제목을 1~120자로 입력하세요.");
  if ((form.description?.length ?? 0) > 2000) throw new Error("설명은 최대 2000자입니다.");
  const tags = [
    ...new Set([...(form.tags ?? []), ...(form.tagText?.trim() ? [form.tagText.trim().toLowerCase()] : [])]),
  ];
  if (tags.length > 30 || tags.some((t) => !Value.Check(Tag, t)))
    throw new Error("태그는 숨은 문자 없이 1~200자씩 최대 30개입니다.");
  const parts = (form.gidText ?? (form.gids ?? []).join(", ")).split(/[,\s]+/).filter(Boolean);
  if (parts.length > 5 || parts.some((p) => !/^[1-9]\d{0,14}$/.test(p) || !Number.isSafeInteger(Number(p))))
    throw new Error("올바른 GID를 최대 5개 입력하세요.");
  const seriesId = form.seriesId || null;
  const episode = form.episodeText ?? String(form.episodeNo ?? 1);
  if (seriesId && (!/^\d{1,3}$/.test(episode) || Number(episode) > 999))
    throw new Error("회차는 0~999 사이의 정수로 입력하세요.");
  return {
    title,
    description: form.description ?? "",
    tags,
    gids: [...new Set(parts.map(Number))],
    seriesId,
    episodeNo: seriesId ? Number(episode) : null,
  };
}

function validForm(v: unknown): v is VideoFormValue {
  return (
    record(v) &&
    ["title", "description", "tagText", "gidText", "episodeText"].every(
      (k) => v[k] === undefined || typeof v[k] === "string",
    ) &&
    (v.tags === undefined || (Array.isArray(v.tags) && v.tags.every((t) => typeof t === "string"))) &&
    (v.gids === undefined || (Array.isArray(v.gids) && v.gids.every((g) => integer(g) && g > 0))) &&
    (v.seriesId == null || typeof v.seriesId === "string") &&
    (v.episodeNo == null || integer(v.episodeNo, 999))
  );
}
function validTicket(v: unknown): v is UploadTicket {
  return (
    Value.Check(ROUTES["videos.create"].responses[201], v) &&
    Number.isFinite(Date.parse(v.expiresAt)) &&
    /^https?:\/\//.test(v.uploadUrl) &&
    v.uploadToken.length > 0
  );
}
function validRow(v: unknown): v is UploadRow {
  return (
    record(v) &&
    typeof v.id === "string" &&
    !!v.id &&
    typeof v.name === "string" &&
    integer(v.size) &&
    integer(v.modified) &&
    validForm(v.form) &&
    phases.includes(v.phase as UploadPhase) &&
    typeof v.target === "boolean" &&
    integer(v.done) &&
    v.done <= v.size &&
    (v.ticket === undefined || validTicket(v.ticket)) &&
    (v.state === undefined || Value.Check(VideoState, v.state)) &&
    (v.error === undefined || typeof v.error === "string") &&
    (v.legacy === undefined || typeof v.legacy === "boolean") &&
    (v.retryOf === undefined || typeof v.retryOf === "string")
  );
}

/** One page owns one account's queue; only this runner may create or PATCH an upload. */
export class UploadQueue {
  private snapshot: QueueSnapshot = { rows: [], running: false, stopReason: "", storageWarning: "" };
  private listeners = new Set<() => void>();
  private alive = true;
  private paused = true;
  private pauseVersion = 0;
  private task: Promise<void> | null = null;
  private controller: AbortController | null = null;
  private activeId: string | null = null;
  private cancelling = new Set<string>();
  private checking = new Map<string, Promise<void>>();
  private preserveStorage = false;
  constructor(
    private client: ApiClient,
    readonly userId: number,
    private changed: () => void = () => {},
  ) {
    const raw = readPref(`volgo.upload.${userId}`);
    if (!raw || raw === "null") return;
    try {
      const saved: unknown = JSON.parse(raw);
      let rows: UploadRow[];
      if (
        record(saved) &&
        saved.version === 2 &&
        Array.isArray(saved.rows) &&
        saved.rows.every(validRow) &&
        new Set(saved.rows.map((r) => r.id)).size === saved.rows.length
      )
        rows = saved.rows;
      else if (
        record(saved) &&
        typeof saved.name === "string" &&
        integer(saved.size) &&
        integer(saved.modified) &&
        validTicket(saved.ticket)
      ) {
        rows = [
          {
            id: crypto.randomUUID(),
            name: saved.name,
            size: saved.size,
            modified: saved.modified,
            form: { title: saved.name.replace(/\.[^.]+$/, "").slice(0, 120) },
            ticket: saved.ticket,
            phase: "missing",
            target: true,
            done: 0,
            rate: 0,
            legacy: true,
          },
        ];
      } else throw new Error("invalid saved upload");
      this.snapshot.rows = rows.map((r) => ({
        ...r,
        file: undefined,
        rate: 0,
        phase:
          r.phase === "creating"
            ? "ambiguous"
            : ["uploading", "queued", "paused", "draft"].includes(r.phase)
              ? "missing"
              : r.phase,
      }));
      this.save();
    } catch {
      this.preserveStorage = true;
      this.snapshot.storageWarning =
        "저장된 업로드 정보를 읽지 못했습니다. 기존 값은 보존했습니다. 이 화면의 새 작업은 저장되지 않습니다.";
    }
  }
  getSnapshot = () => this.snapshot;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  private save(onlyId?: string) {
    if (this.preserveStorage) return;
    let latestRows: UploadRow[] | undefined;
    try {
      const latest: unknown = JSON.parse(readPref(`volgo.upload.${this.userId}`) ?? "null");
      if (record(latest) && latest.version === 2 && Array.isArray(latest.rows) && latest.rows.every(validRow))
        latestRows = latest.rows;
    } catch {
      // A missing or unreadable value cannot contribute a confirmed registration.
    }
    if (this.alive && latestRows) {
      this.snapshot.rows = this.snapshot.rows.map((row) => {
        const confirmed = latestRows.find((saved) => saved.id === row.id);
        if (row.phase !== "ambiguous" || row.ticket || !confirmed?.ticket) return row;
        // A previous page may receive its create response after this page has restored the draft.
        return { ...row, ticket: confirmed.ticket, phase: row.file ? "paused" : "missing", error: undefined };
      });
    }
    let rows = this.snapshot.rows
      .filter((r) => !["published", "cancelled"].includes(r.phase))
      .map(({ file: _file, rate: _rate, duplicate: _duplicate, ...r }) => r);
    if (!this.alive) {
      if (!onlyId || !latestRows?.some((row) => row.id === onlyId)) return;
      const incoming = rows.find((r) => r.id === onlyId);
      rows = latestRows.filter((r) => r.id !== onlyId);
      if (incoming) rows.push(incoming);
    }
    if (!writePref(`volgo.upload.${this.userId}`, JSON.stringify({ version: 2, rows })))
      this.snapshot.storageWarning =
        "이어 올리기 정보를 저장하지 못했습니다. 이 화면에서는 계속할 수 있지만 닫으면 복구 정보가 사라질 수 있습니다.";
  }
  private emit(persist = true) {
    if (persist) this.save();
    this.snapshot = { ...this.snapshot, rows: [...this.snapshot.rows] };
    if (this.alive) for (const listener of this.listeners) listener();
  }
  private update(id: string, patch: Partial<UploadRow>, persist = true) {
    this.snapshot.rows = this.snapshot.rows.map((r) => (r.id === id ? { ...r, ...patch } : r));
    if (!this.alive && persist) this.save(id);
    this.emit(persist);
  }
  private row(id: string) {
    return this.snapshot.rows.find((r) => r.id === id);
  }
  add(files: File[], form?: VideoFormValue) {
    const ids: string[] = [];
    for (const file of files) {
      const id = crypto.randomUUID();
      ids.push(id);
      const error = fileError(file) ?? undefined;
      this.snapshot.rows.push({
        id,
        name: file.name,
        size: file.size,
        modified: file.lastModified,
        file,
        form: form ?? { title: file.name.replace(/\.[^.]+$/, "").slice(0, 120), tags: [], gids: [] },
        phase: error ? "error" : "draft",
        target: false,
        done: 0,
        rate: 0,
        error,
        duplicate: this.snapshot.rows.some((r) => matchesFile(r, file)),
      });
    }
    this.emit();
    return ids;
  }
  addRetry(form: VideoFormValue, name: string, size: number, retryOf: string) {
    const existing = this.snapshot.rows.find(
      (r) => r.retryOf === retryOf && !["published", "cancelled"].includes(r.phase),
    );
    if (existing) return existing.id;
    const id = crypto.randomUUID();
    this.snapshot.rows.push({
      id,
      name,
      size,
      retryOf,
      modified: 0,
      form,
      phase: "missing",
      target: false,
      done: 0,
      rate: 0,
    });
    this.emit();
    return id;
  }
  edit(id: string, form: VideoFormValue) {
    const row = this.row(id);
    if (row && editable(row))
      this.update(id, {
        form,
        error: fileError(row) ?? undefined,
        phase: row.target ? "queued" : row.file ? "draft" : "missing",
      });
  }
  apply(forms: Map<string, VideoFormValue>) {
    this.snapshot.rows = this.snapshot.rows.map((r) =>
      forms.has(r.id) && editable(r) ? { ...r, form: forms.get(r.id)!, error: fileError(r) ?? undefined } : r,
    );
    this.emit();
  }
  remove(id: string) {
    const row = this.row(id);
    if (!row || !editable(row)) return;
    this.snapshot.rows = this.snapshot.rows.filter((r) => r.id !== id);
    this.emit();
  }
  move(id: string, direction: -1 | 1) {
    const slots = this.snapshot.rows.map((r, i) => (editable(r) ? i : -1)).filter((i) => i >= 0);
    const pos = slots.indexOf(this.snapshot.rows.findIndex((r) => r.id === id));
    const from = slots[pos],
      to = slots[pos + direction];
    if (from === undefined || to === undefined) return;
    const rows = [...this.snapshot.rows];
    [rows[from], rows[to]] = [rows[to]!, rows[from]!];
    this.snapshot.rows = rows;
    this.emit();
  }
  sort() {
    const sorted = this.snapshot.rows
      .filter(editable)
      .sort((a, b) => a.name.localeCompare(b.name, "ko", { numeric: true }));
    this.snapshot.rows = this.snapshot.rows.map((r) => (editable(r) ? sorted.shift()! : r));
    this.emit();
  }
  attach(id: string, file: File, fresh = false) {
    const row = this.row(id);
    if (!row) return false;
    const error = fileError(file);
    if (error || (!fresh && !matchesFile(row, file))) {
      this.update(id, {
        error: error ?? "이름·크기·수정 시각이 다른 파일입니다. 같은 파일을 다시 선택하세요.",
      });
      return false;
    }
    this.update(id, {
      file,
      ...(fresh ? { name: file.name, size: file.size, modified: file.lastModified } : {}),
      error: undefined,
      phase: row.ticket ? "paused" : "draft",
    });
    return true;
  }
  attachMany(files: File[]) {
    const unmatched: string[] = [];
    const candidates = this.snapshot.rows.filter(needsFile);
    for (const file of files) {
      const rows = candidates.filter((r) => matchesFile(r, file));
      if (
        rows.length !== 1 ||
        files.filter((f) => matchesFile(rows[0] ?? { name: "", size: 0, modified: 0 }, f)).length !== 1
      )
        unmatched.push(file.name);
      else this.attach(rows[0]!.id, file);
    }
    return unmatched;
  }
  pause(reason = "") {
    this.paused = true;
    this.pauseVersion++;
    this.snapshot.stopReason = reason;
    this.controller?.abort();
    this.emit();
  }
  close() {
    this.pause();
    this.alive = false;
  }
  activate() {
    this.alive = true;
  }
  include(ids: string[]) {
    this.snapshot.rows = this.snapshot.rows.map((row) =>
      ids.includes(row.id) && editable(row) ? { ...row, target: true, phase: "queued" } : row,
    );
    this.emit();
  }
  async start(ids?: string[]) {
    if (!this.alive || !this.userId) return;
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      this.pause("오프라인입니다. 연결을 확인한 뒤 이어 올리기를 눌러 주세요.");
      return;
    }
    const requested = new Set(
      ids ?? this.snapshot.rows.filter((r) => !this.snapshot.running || r.target).map((r) => r.id),
    );
    this.paused = false;
    this.snapshot.stopReason = "";
    this.snapshot.rows = this.snapshot.rows.map((r) => {
      if (ids ? !ids.includes(r.id) : this.snapshot.running && !r.target) return r;
      if (
        [
          "ambiguous",
          "creating",
          "uploading",
          "transferred",
          "processing",
          "published",
          "failed",
          "cancel-failed",
          "cancelled",
        ].includes(r.phase) ||
        this.cancelling.has(r.id)
      )
        return r;
      return { ...r, target: true, phase: "queued" as const, error: undefined };
    });
    this.emit();
    if (this.task) {
      await this.task;
      if (!this.alive || this.paused) return;
      this.snapshot.rows = this.snapshot.rows.map((r) =>
        requested.has(r.id) && r.phase === "paused" ? { ...r, phase: "queued" } : r,
      );
    }
    if (!this.alive || this.paused) return;
    if (!this.task) {
      const task = this.run().finally(() => {
        if (this.task === task) this.task = null;
        this.snapshot.running = false;
        this.emit();
      });
      this.task = task;
    }
    await this.task;
  }
  private globalError(error: unknown): string | null {
    if (typeof navigator !== "undefined" && !navigator.onLine)
      return "오프라인입니다. 연결 후 이어 올리기를 눌러 주세요.";
    if (
      error instanceof ApiError &&
      isAccessRejection(error.status) &&
      (error.status !== 403 || error.code !== "POLICY_BLOCKED")
    )
      return "로그인 또는 접근 권한을 확인해야 합니다. 확인 후 이어 올려 주세요.";
    if (error instanceof UploadError && error.status === 507)
      return "서버 저장 공간이 부족해 전체 전송을 멈췄습니다. 공간 확보 후 이어 올려 주세요.";
    if (error instanceof UploadError && error.status >= 500)
      return "전송 서버에 연결하지 못해 전송을 멈췄습니다. 잠시 후 이어 올려 주세요.";
    if (error instanceof UploadError && isAccessRejection(error.status))
      return "업로드 토큰이 거절되어 전송을 멈췄습니다. 상태를 확인하고 만료된 업로드는 취소 후 새로 올려 주세요.";
    return null;
  }
  private async run() {
    this.snapshot.running = true;
    this.emit();
    while (this.alive && !this.paused) {
      const row = this.snapshot.rows.find(
        (r) => r.target && r.phase === "queued" && !this.cancelling.has(r.id),
      );
      if (!row) break;
      const id = row.id;
      this.activeId = id;
      const controller = new AbortController();
      this.controller = controller;
      try {
        let current = row;
        if (current.ticket) {
          await this.check(id);
          current = this.row(id)!;
          if (!this.alive || this.paused || controller.signal.aborted || this.cancelling.has(id)) continue;
          if (current.state?.status !== "uploading" || current.done === current.size) continue;
          if (Date.parse(current.ticket!.expiresAt) <= Date.now())
            throw new UploadError("업로드가 만료되었습니다. 기존 업로드 취소 후 새로 올려 주세요.", 410);
        }
        if (!current.file) {
          this.update(id, { phase: "missing", error: "같은 파일을 다시 선택하세요." });
          continue;
        }
        const invalid = fileError(current.file);
        if (invalid) throw new Error(invalid);
        if (!current.ticket) {
          const form = finalizeForm(current.form);
          const validation = await this.client.call("videos.validateTags", {
            body: { tags: form.tags ?? [] },
          });
          if (validation.blockedTags.length)
            throw new Error(`차단된 태그는 올릴 수 없습니다: ${validation.blockedTags.join(", ")}`);
          if (!this.alive || this.paused || controller.signal.aborted || this.cancelling.has(id)) continue;
          // A draft can change during policy validation; validate its final snapshot again on the next pass.
          if (this.row(id)?.form !== current.form) continue;
          this.update(id, { phase: "creating", form, error: undefined });
          try {
            const ticket = await this.client.call("videos.create", {
              body: { ...form, fileName: current.name, sizeBytes: current.size },
            });
            if (!validTicket(ticket)) throw new Error("등록 응답을 읽지 못했습니다.");
            this.update(id, { ticket, phase: "paused" });
            this.changed();
          } catch (error) {
            const definite = error instanceof ApiError && error.status >= 400 && error.status < 500;
            this.update(id, {
              phase: definite ? "error" : "ambiguous",
              error: definite
                ? message(error)
                : "등록 결과를 확인하지 못했습니다. 중복 등록을 막기 위해 잠갔습니다. 올린 영상에서 확인하세요.",
            });
            const reason = this.globalError(error);
            if (reason) this.pause(reason);
            continue;
          }
          current = this.row(id)!;
        }
        if (!this.alive || this.paused || controller.signal.aborted || this.cancelling.has(id)) continue;
        this.update(id, { phase: "uploading", error: undefined, rate: 0 });
        await uploadFile(this.client, current.ticket!, current.file!, controller.signal, (done, rate) =>
          this.update(id, { done, rate }, false),
        );
        this.update(id, { phase: "transferred", done: current.size, rate: 0 });
        this.changed();
        if (this.alive) await this.check(id);
      } catch (error) {
        if (controller.signal.aborted) {
          if (this.row(id)?.phase === "uploading") this.update(id, { phase: "paused", rate: 0 });
        } else {
          const latest = this.row(id)!;
          this.update(id, {
            phase: latest.phase === "transferred" ? "transferred" : latest.ticket ? "paused" : "error",
            error: message(error),
            rate: 0,
          });
          const reason = this.globalError(error);
          if (reason) this.pause(reason);
        }
      } finally {
        this.controller = null;
        this.activeId = null;
      }
    }
  }
  async check(id: string) {
    if (this.checking.has(id)) return this.checking.get(id);
    const row = this.row(id);
    if (!this.alive || !row?.ticket || this.cancelling.has(id)) return;
    const ticket = row.ticket;
    const work = (async () => {
      try {
        const state = await this.client.call("videos.status", { params: { id: ticket.videoId } });
        if (!this.alive || this.row(id)?.ticket !== ticket || this.cancelling.has(id)) return;
        const current = this.row(id)!;
        const phase: UploadPhase =
          current.phase === "cancel-failed"
            ? "cancel-failed"
            : state.status === "uploading"
              ? current.done === current.size
                ? "transferred"
                : this.activeId === id &&
                    current.phase === "uploading" &&
                    !this.paused &&
                    !this.controller?.signal.aborted
                  ? "uploading"
                  : current.file
                    ? "paused"
                    : "missing"
              : state.status === "published"
                ? "published"
                : state.status === "failed" || state.status === "removed"
                  ? "failed"
                  : "processing";
        this.update(id, {
          state,
          phase,
          error: undefined,
          ...(phase === "processing" || phase === "published" ? { done: current.size } : {}),
        });
        if (row.legacy || phase === "published") {
          try {
            const detail = await this.client.call("videos.get", { params: { id: ticket.videoId } });
            if (this.alive && this.row(id)?.ticket === ticket && !this.cancelling.has(id))
              this.update(id, {
                codec: detail.videoCodec,
                ...(row.legacy
                  ? {
                      legacy: false,
                      form: {
                        title: detail.title,
                        description: detail.description,
                        tags: detail.tags,
                        gids: detail.links.map((g) => g.gid),
                        seriesId: detail.series?.id ?? null,
                        episodeNo: detail.series?.episodeNo ?? null,
                      },
                    }
                  : {}),
              });
          } catch {
            /* State remains authoritative when optional metadata cannot be read. */
          }
        }
        if (phase === "published" || phase === "failed") this.changed();
      } catch (error) {
        if (this.alive && this.row(id)?.ticket === ticket && !this.cancelling.has(id)) {
          this.update(id, {
            error: `상태 확인 실패: ${message(error)}. 새로 등록하지 않고 다시 확인합니다.`,
          });
          const reason = this.globalError(error);
          if (reason) this.pause(reason);
        }
        throw error;
      }
    })().finally(() => {
      this.checking.delete(id);
    });
    this.checking.set(id, work);
    return work;
  }
  async poll() {
    const rows = this.snapshot.rows.filter(
      (r) =>
        r.ticket &&
        r.id !== this.activeId &&
        r.phase !== "uploading" &&
        !["published", "cancelled", "failed"].includes(r.phase),
    );
    await Promise.allSettled(rows.map((r) => this.check(r.id)));
  }
  async cancel(id: string, restart = false) {
    if (this.cancelling.has(id)) return;
    const resume = this.activeId === id && this.snapshot.running && !this.paused;
    this.cancelling.add(id);
    if (this.activeId === id) this.pause();
    const pauseVersion = this.pauseVersion;
    let deleted = false;
    this.emit();
    try {
      if (this.activeId === id && this.task) await this.task;
      const row = this.row(id);
      if (!row?.ticket || !this.alive) return;
      const ticket = row.ticket;
      // API deletion is authoritative even when media is down or its upload token has expired.
      await this.client.call("videos.delete", { params: { id: ticket.videoId } });
      deleted = true;
      if (this.alive) void this.client.media(ticket.uploadUrl, ticket.uploadToken, "DELETE").catch(() => {});
      this.update(
        id,
        restart
          ? {
              ticket: undefined,
              state: undefined,
              phase: row.file ? "draft" : "missing",
              target: false,
              done: 0,
              rate: 0,
              error: undefined,
            }
          : { phase: "cancelled", file: undefined, target: false, rate: 0, error: undefined },
      );
      this.changed();
    } catch (error) {
      this.update(id, {
        phase: "cancel-failed",
        error: `취소를 확인하지 못했습니다: ${message(error)}. 상태 확인 또는 취소 재시도가 필요합니다.`,
        rate: 0,
      });
      const reason = this.globalError(error);
      if (reason) this.pause(reason);
    } finally {
      this.cancelling.delete(id);
      this.emit();
      if (deleted && resume && this.alive && this.pauseVersion === pauseVersion)
        void this.start(this.snapshot.rows.filter((r) => r.target && r.phase === "queued").map((r) => r.id));
    }
  }
  retryFailed(id: string) {
    const row = this.row(id);
    if (row?.phase !== "failed") return;
    this.update(id, {
      ticket: undefined,
      state: undefined,
      file: undefined,
      modified: 0,
      phase: "missing",
      target: false,
      done: 0,
      rate: 0,
      error: "같은 정보를 유지했습니다. 파일을 다시 선택하면 0%부터 새로 올립니다.",
    });
  }
}
